import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { FloatLabelType, MatFormFieldAppearance } from '@angular/material/form-field';
import { TextMaskConfig } from 'angular2-text-mask/src/angular2TextMask';
import * as moment from 'moment';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

@AutoUnsubscribe({ includeArrays: true })
@Component({
    selector: 'tyl-datepicker',
    templateUrl: './tyl-datepicker.component.html',
    styleUrls: ['./tyl-datepicker.component.css'],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ]
})
export class TylDatepickerComponent implements OnInit, OnChanges, OnDestroy {
    constructor() { }

    displayControl = new FormControl('', { updateOn: 'blur' });
    hiddenControl = new FormControl('', { updateOn: 'blur' });
    innerValue: moment.Moment;
    isError = false;
    get localValue(): string | Date | moment.Moment {
        return this.innerValue;
    }

    set localValue(v: string | Date | moment.Moment) {
        let newVal: moment.Moment = null;
        if (v != null && v != '') {
            if (moment.isMoment(v)) {
                newVal = v;
            }
            else {
                if (v.toString().indexOf('T') > -1) {
                    newVal = moment(v);
                } else {
                    newVal = moment(v, 'MM/DD/YYYY');
                }
            }
        }
        this.innerValue = newVal;
    }

    @Input() label = '';
    @Input() value: string | Date | moment.Moment;
    @Input() id: string;
    @Input() floatLabel:FloatLabelType = 'auto';
    @Input() stopPropagation = false;
    @Input() isRequired: boolean;
    @Input() truncateLabel: boolean;
    @Input() showLabelAsHint = false;
    @Input() disabled = false;
    @Input() errorMessage = '';
    @Input() appearance:MatFormFieldAppearance = 'outline';
    @Input() hideRequiredMarker = false;
    @Input() allowEnter = false;	// If this is true, pressing enter updates the model
    @Input() fullWidth: boolean;
    @Input() tabIndex = 0;
    @Output() valueChange = new EventEmitter();
    @Output() dateInputChange = new EventEmitter<ElementRef>();
    @Input() postLabelSpace = false;
    @Input() clearable = false;
    @Input() customHint = '';
    @Input() showTodayButton = false;

    initialized = false;
    hovering = false;
    displayValue: string;
    updated = false;
    private _dateInput: ElementRef;
    public mask: TextMaskConfig = {
        guide: true,
        showMask: false,
        mask: (rawValue: string) => {
            if (rawValue) {
                const maskArray = [];
                maskArray.push(/\d/);
                let secondDashPosition = 4;
                if (rawValue.length > 1
                    && rawValue.charAt(1) == '/') {
                    secondDashPosition -= 1;
                    maskArray.push('/');
                } else {
                    maskArray.push(/\d/);
                    maskArray.push('/');
                }
                maskArray.push(/\d/);
                if (rawValue.length > secondDashPosition
                    && rawValue.charAt(secondDashPosition) == '/'
                    && rawValue.charAt(secondDashPosition - 1).match(/\d/)) {
                    maskArray.push('/');
                } else {
                    maskArray.push(/\d/);
                    maskArray.push('/');
                }
                maskArray.push(/\d/);
                maskArray.push(/\d/);
                maskArray.push(/\d/);
                maskArray.push(/\d/);
                return maskArray;
            } else {
                return [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
            }
        }
    };

    ngOnInit() {
        this.setDate(this.value);
        if (this.id == null || this.id == '') {
            this.id = this.label.replace(' ', '');
        }
    }

    get dateInput() {
        return this._dateInput;
    }

    @ViewChild('dateInput', { static: true }) set dateInput(value: ElementRef) {
        this._dateInput = value;
        this.dateInputChange.emit(this._dateInput);
    }

    setParentValue() {
        if (moment.isMoment(this.value)) {
            this.value = this.localValue;
        }
        else if (this.value instanceof Date) {
            if (this.innerValue != null) {
                this.value = this.innerValue.toDate();
            }
            else {
                this.value = null;
            }
        }
        else {
            if (this.innerValue != null) {
                this.value = this.innerValue.toDate();
            }
            else {
                this.value = '';
            }
        }
        this.valueChange.emit(this.value);
        this.updated = true;
    }

    enterPressed() {
        this.dateInput.nativeElement.blur();
        // this.dateInput.nativeElement.focus();  // ONE-5782 - if I use the [Tab] key to navigate to the next field, I receive the correct error message (Invalid Date). However, if I use the [Enter] key, it either just blanks out the field with no error or gives an "Required Field" error.
    }

    calendarDateChanged() {
        if (this.innerValue != null) {
            this.displayValue = this.innerValue.format('MM/DD/YYYY');
        }
        else {
            this.displayValue = '';
        }
        this.setParentValue();
        this.validate();
    }

    displayDateChanged() {
        if (!this.updated && this.initialized) {
            this.localValue = this.displayValue;
            this.setParentValue();
            this.validate();
        }
    }

    setDate(value: string | Date | moment.Moment) {
        setTimeout(() => {
            if (value != null && value != '') {
                this.localValue = value;
                if (value.toString().toLowerCase() != 'invalid date') {
                    this.displayValue = this.innerValue.format('MM/DD/YYYY');
                }
            }
            else {
                this.localValue = null;
                this.displayValue = '';
            }
            this.initialized = true;
        }, 0);
    }

    todayClick() {
        // PRE: the today button is clicked
        // POST: sets the date to today
        const today = new Date();
        this.setDate(today);
        this.valueChange.emit(today);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.value) {
            this.setDate(changes.value.currentValue);
        }
    }

    clearIfInvalid() {
        // POST: clears the date value if it is in valid
        const dateValueFromDisplay = new Date(this.displayValue);
        if (dateValueFromDisplay.toString() == 'Invalid Date') {
            this.clear();
        } else {
            let momentDatevaluefromDisplay: moment.Moment;
            if (this.displayValue.indexOf('T') > -1) {
                momentDatevaluefromDisplay = moment(this.displayValue);
            } else {
                momentDatevaluefromDisplay = moment(this.displayValue, 'MM/DD/YYYY');
            }
            if (momentDatevaluefromDisplay != null && momentDatevaluefromDisplay.toString().toLocaleLowerCase() == 'invalid date') {
                this.displayValue = '';
                this.displayDateChanged();
            }
        }
    }

    validate() {
        this.isError = false;
        if (new Date(this.displayValue) > new Date('06/06/2079')) {
            this.isError = true;
        }
    }

    ngOnDestroy() { }

    @HostListener('mouseenter', ['$event'])
    mouseEnter(event) {
        // PRE: the mouse enters this component
        // POST: sets hovering to true to allow showing the red X
        this.hovering = true;
        event.stopPropagation();
    }

    @HostListener('mouseleave')
    mouseLeave() {
        // PRE: the mouse leaves the component
        // POST: sets hovering to false to disable showing the red X
        this.hovering = false;
    }

    clear() {
        // POST: clears the date and display
        this.displayValue = '';
        this.updated = false;
        this.displayDateChanged();
    }
}
